import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import HomePage from './components/HomePage/HomePage';
import Services from './components/Pages/Services/Services';
import PrivateCleaning from './components/Pages/Services/PrivateCleaning';
import CommercialCleaning from './components/Pages/Services/CommercialCleaning';
import MoveInMoveOut from './components/Pages/Services/MoveInMoveOut';
import About from './components/Pages/About/About';
import Contact from './components/Pages/Contact/Contact';
import Careers from './components/Pages/Careers/Careers';
import AuthCallback from './components/Auth/AuthCallback';
import { initMockData } from './utils/mockData';
import './App.css';

// Component to redirect to auth app
const AuthRedirect = ({ to }) => {
  const authAppUrl = process.env.REACT_APP_AUTH_APP_URL || 'https://app.rentplus.dk';
  const currentUrl = window.location.origin;
  const returnPath = window.location.pathname;
  const redirectUrl = `${authAppUrl}/${to}?redirect=${encodeURIComponent(currentUrl)}&returnPath=${encodeURIComponent(returnPath)}`;
  
  useEffect(() => {
    window.location.href = redirectUrl;
  }, [redirectUrl]);
  
  return null;
};

// Component to handle auth callback - processes token from URL and redirects
const AuthCallbackHandler = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    // Get token and user data from URL params (from auth app redirect)
    const token = searchParams.get('token');
    const userId = searchParams.get('userId');
    const userRole = searchParams.get('userRole');
    const returnPath = searchParams.get('returnPath');
    
    if (token && userId && userRole) {
      // Store authentication data immediately
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', userRole);
      localStorage.setItem('userId', userId);
      localStorage.setItem('token', token);
      
      // Get return path or default to role-based dashboard
      let finalPath = returnPath;
      if (!finalPath || finalPath === '/' || finalPath === '/login' || finalPath === '/register') {
        finalPath = userRole === 'admin' ? '/admin' : 
                    userRole === 'cleaner' ? '/cleaner' : '/client';
      }
      
      // Remove query params from URL and redirect
      window.history.replaceState({}, '', finalPath);
      // Small delay to ensure localStorage is set before redirect
      setTimeout(() => {
        window.location.href = finalPath;
      }, 100);
    } else {
      // No token, redirect to home
      window.location.href = '/';
    }
  }, [searchParams]);
  
  return <AuthCallback />;
};

function App() {
  // Initialize mock data for standalone testing
  useEffect(() => {
    initMockData();
  }, []);

  return (
    <LanguageProvider>
      <ToastProvider>
        <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/private-cleaning" element={<PrivateCleaning />} />
          <Route path="/services/commercial-cleaning" element={<CommercialCleaning />} />
          <Route path="/services/move-in-move-out" element={<MoveInMoveOut />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/auth/callback" element={<AuthCallbackHandler />} />
          {/* Auth routes - redirect to auth app */}
          <Route path="/login" element={<AuthRedirect to="login" />} />
          <Route path="/register" element={<AuthRedirect to="register" />} />
        </Routes>
      </Router>
      </ToastProvider>
    </LanguageProvider>
  );
}

export default App;


